// Pure helpers used by MarkdownRenderer. Kept here (not inline in the
// component) so they can be unit tested.

// ── GFM pipe tables ──────────────────────────────────────────────────────────

// Split one table row into its cells. Leading/trailing pipes are optional,
// a pipe can be kept in a cell by escaping it as `\|` (also inside `code`),
// and cells are trimmed.
export function splitTableRow(line) {
  let row = line.trim();
  if (row.startsWith("|")) row = row.slice(1);
  if (row.endsWith("|") && !row.endsWith("\\|")) row = row.slice(0, -1);

  const cells = [];
  let current = "";
  for (let i = 0; i < row.length; i++) {
    const ch = row[i];
    if (ch === "\\" && row[i + 1] === "|") {
      current += "|";
      i++;
    } else if (ch === "|") {
      cells.push(current.trim());
      current = "";
    } else {
      current += ch;
    }
  }
  cells.push(current.trim());
  return cells;
}

const DELIMITER_CELL = /^:?-+:?$/;

// Parse the `|---|:---:|---:|` row under a table header. Returns one alignment
// per column ("left" | "center" | "right" | null), or null when the line is
// not a delimiter row.
export function parseTableAlignment(line) {
  if (!line || !line.includes("-")) return null;
  const trimmed = line.trim();
  // A delimiter row needs at least one pipe, unless it is a single column
  // written with both outer pipes; a bare `---` is a horizontal rule.
  if (!trimmed.includes("|")) return null;
  const cells = splitTableRow(trimmed);
  if (!cells.every((c) => DELIMITER_CELL.test(c))) return null;
  return cells.map((c) => {
    const left = c.startsWith(":");
    const right = c.endsWith(":");
    if (left && right) return "center";
    if (right) return "right";
    if (left) return "left";
    return null;
  });
}

// True when lines[index] starts a GFM pipe table: a header row containing a
// pipe, followed by a delimiter row with the same number of columns.
export function isPipeTableStart(lines, index) {
  const header = lines[index];
  const delimiter = lines[index + 1];
  if (!header || !header.includes("|") || delimiter === undefined) return false;
  const align = parseTableAlignment(delimiter);
  if (!align) return false;
  return splitTableRow(header).length === align.length;
}

// Parse the pipe table starting at lines[startIndex]. Body rows run until the
// first blank line or line without a pipe. Rows are padded or cut to the
// header's column count, as GFM does. Returns null if no table starts there.
export function parsePipeTable(lines, startIndex) {
  if (!isPipeTableStart(lines, startIndex)) return null;
  const header = splitTableRow(lines[startIndex]);
  const align = parseTableAlignment(lines[startIndex + 1]);
  const columns = header.length;
  const rows = [];
  let i = startIndex + 2;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim() === "" || !line.includes("|")) break;
    const cells = splitTableRow(line).slice(0, columns);
    while (cells.length < columns) cells.push("");
    rows.push(cells);
    i++;
  }
  return { header, align, rows, nextIndex: i };
}

// ── Heading ids ──────────────────────────────────────────────────────────────

// Plain text of a heading's markdown: keeps the text of code spans (so
// `<YOUR_KEY>` stays), then drops HTML tags, link targets and emphasis.
export function headingPlainText(text) {
  const codeSpans = [];
  return text
    .replace(/`([^`]*)`/g, (_, code) => {
      codeSpans.push(code);
      return `\uE000${codeSpans.length - 1}\uE000`;
    })
    .replace(/<[^>]+>/g, "")
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/(\*\*\*|\*\*|\*|___|__|_)(.+?)\1/g, "$2")
    .replace(/\uE000(\d+)\uE000/g, (_, n) => codeSpans[Number(n)]);
}

// GitHub-style heading slug: lower case, punctuation removed (letters,
// numbers, spaces, `-` and `_` kept), each space turned into a hyphen.
// "5. Idempotency" -> "5-idempotency".
export function slugifyHeading(text) {
  return headingPlainText(text)
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s_-]/gu, "")
    .replace(/\s/g, "-");
}

// Returns a function that slugs headings and, like GitHub, adds -1, -2, ...
// to repeats so every id on the page is unique. Use one per rendered page.
export function createHeadingSlugger() {
  const used = new Set();
  const counts = new Map();
  return function slug(text) {
    const base = slugifyHeading(text) || "section";
    let id = base;
    if (used.has(id)) {
      let n = counts.get(base) || 0;
      do {
        n += 1;
        id = `${base}-${n}`;
      } while (used.has(id));
      counts.set(base, n);
    }
    used.add(id);
    return id;
  };
}

// ── Links ────────────────────────────────────────────────────────────────────

// The target id of a same-page link ("#5-idempotency" -> "5-idempotency"),
// or null for any other link.
export function getSamePageAnchorId(href) {
  if (typeof href !== "string" || !href.startsWith("#") || href.length < 2) {
    return null;
  }
  try {
    return decodeURIComponent(href.slice(1));
  } catch {
    return href.slice(1);
  }
}

// ── Front matter ─────────────────────────────────────────────────────────────

// Remove a leading `---` front-matter block and return the rest unchanged.
export function stripFrontMatter(md) {
  return md
    .replace(/^\uFEFF/, "")
    .replace(/^---\r?\n[\s\S]*?\r?\n---\r?\n?/, "")
    .replace(/^\s+/, "");
}

// ── Lists ────────────────────────────────────────────────────────────────────

// True when `line` continues the previous list item's text: it is indented
// by 2 or 3 spaces, i.e. aligned with the item text after "- " or "1. ", and
// does not start a new block such as another list item, a heading, a code
// fence, a table or a quote.
// "- Never put\n  an API key in a browser" is one item, not an item and a
// paragraph. Lines indented 4+ spaces are left alone, so existing pages that
// indent code-like text under a list item (geosafe/SDK-Android.md) render as
// before.
export function isListContinuation(line) {
  if (typeof line !== "string" || !/^ {2,3}\S/.test(line)) return false;
  const t = line.trim();
  return !(
    /^[-*+]\s/.test(t) ||
    /^\d+\.\s/.test(t) ||
    /^#{1,6}\s/.test(t) ||
    t.startsWith("```") ||
    t.startsWith(">") ||
    t.startsWith("|") ||
    t.startsWith("%%") ||
    t.startsWith("<") ||
    t.startsWith("![") ||
    /^[-_]{3,}$/.test(t)
  );
}
